import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Search, Calendar, Clock, ChevronRight, Tag, ArrowRight, Phone } from 'lucide-react';
import { blogArticles, getFeaturedArticles, getAllCategories } from '@/data/blogArticles';

const BlogList = ({ onArticleClick }) => {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [filteredArticles, setFilteredArticles] = useState(blogArticles);
  
  const handleArticleClick = onArticleClick || ((slug) => navigate(`/blog/${slug}`));
  
  const categories = ['All', ...getAllCategories()];
  const featuredArticles = getFeaturedArticles();

  useEffect(() => {
    let results = blogArticles;
    
    if (selectedCategory !== 'All') {
      results = results.filter(article => article.category === selectedCategory);
    }
    
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      results = results.filter(article => 
        article.title.toLowerCase().includes(term) ||
        article.tags.some(tag => tag.includes(term)) ||
        article.metaDescription.toLowerCase().includes(term)
      );
    }
    
    setFilteredArticles(results);
  }, [searchTerm, selectedCategory]);

  return (
    <div className="min-h-screen bg-[#FDFBF7]">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-[#0A0A0A] via-[#1a1a2e] to-[#0A0A0A] py-16 md:py-24">
        <div className="container mx-auto px-4">
          <motion.div 
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center max-w-4xl mx-auto"
          >
            <span className="inline-block bg-[#FF007A] text-white text-sm font-bold px-4 py-2 rounded-full mb-6">
              LAS VEGAS TOUR GUIDE
            </span>
            <h1 className="font-bebas text-4xl md:text-6xl lg:text-7xl text-white mb-6 tracking-tight">
              VEGAS TOURS <span className="text-[#FFD600]">INSIDER BLOG</span>
            </h1>
            <p className="font-outfit text-lg md:text-xl text-white/80 mb-8 max-w-2xl mx-auto">
              Expert guides, travel tips, and everything you need to plan the perfect Las Vegas tour experience. From Hollywood day trips to hidden gems.
            </p>
            
            {/* Search Bar */}
            <div className="relative max-w-xl mx-auto">
              <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <input
                type="text"
                placeholder="Search tours, destinations, tips..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-12 pr-4 py-4 rounded-xl bg-white/10 backdrop-blur-sm border border-white/20 text-white placeholder-white/50 font-outfit focus:outline-none focus:ring-2 focus:ring-[#FF007A]"
              />
            </div>
          </motion.div>
        </div>
      </section>

      {/* Category Filter */}
      <section className="bg-white border-b border-gray-200 sticky top-16 z-40">
        <div className="container mx-auto px-4">
          <div className="flex items-center gap-2 overflow-x-auto py-4 scrollbar-hide">
            {categories.map((category) => (
              <button
                key={category}
                onClick={() => setSelectedCategory(category)}
                className={`px-4 py-2 rounded-full text-sm font-outfit font-medium whitespace-nowrap transition-all ${
                  selectedCategory === category
                    ? 'bg-[#FF007A] text-white'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                {category}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Featured Articles */}
      {selectedCategory === 'All' && !searchTerm && (
        <section className="py-12 bg-gradient-to-b from-[#FFD600]/10 to-transparent">
          <div className="container mx-auto px-4">
            <div className="flex items-center gap-3 mb-8">
              <span className="w-8 h-1 bg-[#FF007A]"></span>
              <h2 className="font-bebas text-2xl md:text-3xl tracking-tight">FEATURED GUIDES</h2>
            </div>
            
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {featuredArticles.slice(0, 3).map((article, index) => (
                <motion.article
                  key={article.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                  onClick={() => onArticleClick(article.slug)}
                  className="group cursor-pointer bg-white rounded-2xl overflow-hidden border-2 border-[#0A0A0A] shadow-[4px_4px_0px_0px_rgba(10,10,10,1)] hover:shadow-[6px_6px_0px_0px_rgba(255,0,122,1)] hover:border-[#FF007A] transition-all"
                >
                  <div className="relative h-48 overflow-hidden">
                    <img 
                      src={article.heroImage} 
                      alt={article.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                    <span className="absolute top-4 left-4 bg-[#FFD600] text-[#0A0A0A] text-xs font-bold px-3 py-1 rounded-full">
                      FEATURED
                    </span>
                  </div>
                  <div className="p-6">
                    <span className="text-[#FF007A] text-sm font-medium">{article.category}</span>
                    <h3 className="font-bebas text-xl mt-2 mb-3 group-hover:text-[#FF007A] transition-colors line-clamp-2">
                      {article.title}
                    </h3>
                    <p className="font-outfit text-gray-600 text-sm line-clamp-2 mb-4">
                      {article.metaDescription}
                    </p>
                    <div className="flex items-center justify-between text-sm text-gray-500">
                      <div className="flex items-center gap-4">
                        <span className="flex items-center gap-1">
                          <Calendar className="w-4 h-4" />
                          {new Date(article.publishDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                        </span>
                        <span className="flex items-center gap-1">
                          <Clock className="w-4 h-4" />
                          {article.readTime}
                        </span>
                      </div>
                      <ChevronRight className="w-5 h-5 group-hover:translate-x-1 transition-transform text-[#FF007A]" />
                    </div>
                  </div>
                </motion.article>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* All Articles Grid */}
      <section className="py-12">
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-between mb-8">
            <div className="flex items-center gap-3">
              <span className="w-8 h-1 bg-[#00E5FF]"></span>
              <h2 className="font-bebas text-2xl md:text-3xl tracking-tight">
                {searchTerm ? `SEARCH RESULTS` : selectedCategory === 'All' ? 'ALL GUIDES' : selectedCategory.toUpperCase()}
              </h2>
            </div>
            <span className="text-gray-500 font-outfit text-sm">
              {filteredArticles.length} article{filteredArticles.length !== 1 ? 's' : ''}
            </span>
          </div>

          {filteredArticles.length === 0 ? (
            <div className="text-center py-16">
              <p className="font-outfit text-gray-600 text-lg mb-4">No articles found matching your search.</p>
              <button 
                onClick={() => { setSearchTerm(''); setSelectedCategory('All'); }}
                className="text-[#FF007A] font-medium hover:underline"
              >
                Clear filters
              </button>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredArticles.map((article, index) => (
                <motion.article
                  key={article.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: Math.min(index * 0.05, 0.5) }}
                  onClick={() => onArticleClick(article.slug)}
                  className="group cursor-pointer bg-white rounded-xl overflow-hidden border border-gray-200 hover:border-[#FF007A] hover:shadow-lg transition-all"
                >
                  <div className="relative h-40 overflow-hidden">
                    <img 
                      src={article.heroImage} 
                      alt={article.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      loading="lazy"
                    />
                  </div>
                  <div className="p-5">
                    <div className="flex items-center gap-2 mb-2">
                      <span className="text-[#FF007A] text-xs font-medium bg-[#FF007A]/10 px-2 py-1 rounded">
                        {article.category}
                      </span>
                    </div>
                    <h3 className="font-bebas text-lg mb-2 group-hover:text-[#FF007A] transition-colors line-clamp-2">
                      {article.title}
                    </h3>
                    <p className="font-outfit text-gray-600 text-sm line-clamp-2 mb-3">
                      {article.metaDescription}
                    </p>
                    <div className="flex items-center text-xs text-gray-500 gap-3">
                      <span className="flex items-center gap-1">
                        <Clock className="w-3 h-3" />
                        {article.readTime}
                      </span>
                      <span>{new Date(article.publishDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</span>
                    </div>
                  </div>
                </motion.article>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* CTA Section */}
      <section className="bg-[#FF007A] py-12 md:py-16">
        <div className="container mx-auto px-4 text-center">
          <h2 className="font-bebas text-3xl md:text-4xl text-white mb-4">
            READY TO BOOK YOUR TOUR?
          </h2>
          <p className="font-outfit text-white/90 mb-6 max-w-xl mx-auto">
            Experience the #1 rated day trip from Las Vegas. All-inclusive Vegas to Hollywood tour with breakfast and lunch included.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <a 
              href="#booking"
              onClick={(e) => {
                e.preventDefault();
                window.location.hash = 'booking';
              }}
              className="bg-[#FFD600] text-[#0A0A0A] font-bebas text-lg px-8 py-4 rounded-xl hover:bg-white transition-colors"
            >
              BOOK NOW — $215
            </a>
            <a 
              href="tel:+18777418390"
              className="flex items-center gap-2 bg-white/10 text-white font-outfit px-6 py-4 rounded-xl hover:bg-white/20 transition-colors"
            >
              <Phone className="w-5 h-5" />
              1-877-741-8390
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default BlogList;
